import { useState, useEffect } from 'react'
import { SymbolTabs } from './SymbolTabs'
import { StrategyChart } from './StrategyChart'
import { TrendQualBreakdown } from './TrendQualBreakdown'
import { BacktestMetrics } from './BacktestMetrics'
import { StrategyFilterBar } from './StrategyFilterBar'
import type { DateRange, OutcomeFilter, SignalFilter } from './StrategyFilterBar'
import {
  fetchBacktestResults,
  DEMO_BACKTEST,
  type BacktestResult,
} from '../lib/supabase'

const FALLBACK_SYMBOLS = ['SPY', 'DIA', 'QQQ']

export function StrategyView() {
  const [symbols, setSymbols] = useState<string[]>(FALLBACK_SYMBOLS)
  const [selectedSymbol, setSelectedSymbol] = useState('SPY')
  const [backtestData, setBacktestData] = useState<Record<string, BacktestResult>>({})
  const [isDemo, setIsDemo] = useState(false)
  const [loading, setLoading] = useState(true)

  const [dateRange, setDateRange] = useState<DateRange>({ start: '', end: '' })
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>('all')
  const [signalFilter, setSignalFilter] = useState<SignalFilter>('all')

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      const results = await fetchBacktestResults('trendqual', FALLBACK_SYMBOLS)
      if (!cancelled) {
        if (results.length > 0) {
          const map: Record<string, BacktestResult> = {}
          results.forEach(r => { map[r.symbol] = r })
          const liveSymbols = results.map(r => r.symbol)
          setSymbols(liveSymbols)
          setSelectedSymbol(liveSymbols[0])
          setBacktestData(map)
          setIsDemo(false)
        } else {
          setSymbols(FALLBACK_SYMBOLS)
          setSelectedSymbol('SPY')
          setBacktestData(DEMO_BACKTEST)
          setIsDemo(true)
        }
        setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [])

  const handleReset = () => {
    setDateRange({ start: '', end: '' })
    setOutcomeFilter('all')
    setSignalFilter('all')
  }

  const current = backtestData[selectedSymbol]

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
      </div>
    )
  }

  if (!current) {
    return (
      <div className="empty-state">
        <div className="empty-state-text">No backtest data for {selectedSymbol}</div>
      </div>
    )
  }

  const { daily_scores, trades, weights, thresholds } = current.parameters
  const latestScore = daily_scores.length > 0 ? daily_scores[daily_scores.length - 1] : null

  return (
    <div className="strategy-view">
      {isDemo && (
        <div style={{ fontSize: '0.7rem', color: '#f59e0b', marginBottom: 'var(--space-sm)' }}>⚠ Demo backtest data — run export_strategy_backtest.py to load live results</div>
      )}
      <SymbolTabs symbols={symbols} selected={selectedSymbol} onSelect={setSelectedSymbol} />

      <StrategyFilterBar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        outcomeFilter={outcomeFilter}
        onOutcomeFilterChange={setOutcomeFilter}
        signalFilter={signalFilter}
        onSignalFilterChange={setSignalFilter}
        onReset={handleReset}
      />

      <StrategyChart
        scores={daily_scores}
        trades={trades}
        outcomeFilter={outcomeFilter}
        signalFilter={signalFilter}
        dateRange={dateRange}
      />

      <div className="strategy-bottom-grid">
        <TrendQualBreakdown
          latestScore={latestScore}
          weights={weights}
          thresholds={thresholds}
        />
        <BacktestMetrics metrics={current.metrics} />
      </div>
    </div>
  )
}
